/**
 * @file billingToast.tsx
 * @brief A billing refusal as a toast with a button to the screen that fixes it.
 *
 * @details
 * Callers pass the server's code; billingErrors.ts decides the words and the
 * destination for this user. Returns false when the code is not a billing one,
 * so the caller can show its own message.
 *
 * @author DES
 * @date 17-10-2026
 */

import { ToastAction } from "@/components/ui/Toast";
import { toast } from "@/shared/hooks/useToast";
import { billingErrorView, type BillingAudience, type BillingErrorDetails } from "./billingErrors";

export function showBillingErrorToast(opts: {
  code: string | null | undefined;
  audience: BillingAudience;
  serverMessage?: string | null;
  /** B20: `resetMode` / `payAsYouGo` of a FREE_LIMIT_REACHED body. */
  details?: BillingErrorDetails;
  navigate: (to: string) => void;
}): boolean {
  const view = billingErrorView(opts.code, opts.audience, opts.serverMessage, opts.details);
  if (!view) return false;
  toast({
    variant: "destructive",
    title: view.title,
    description: view.message,
    duration: 12_000,
    action: view.action ? (
      <ToastAction altText={view.action.label} onClick={() => opts.navigate(view.action!.to)}>
        {view.action.label}
      </ToastAction>
    ) : undefined,
  });
  return true;
}
